import { KeyValue } from "@angular/common";
import { Component, ElementRef, Injector, OnInit } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { PersonFlag } from "@common/ADAPT.Common.Model/person/person-flag.enum";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { IGroupedData } from "@common/lib/utilities/grouped-data.interface";
import { UserService } from "@common/user/user.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { ColumnSize, ComponentSize } from "@common/ux/layout/layout.interface";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityPage, IActivityEntityType, IActivityGroup, IActivityRoutes, IActivityStatusSection, ICulturalActivitiesGroupedByStatusAndType } from "@org-common/lib/activities/activities-metadata";
import { IActivityFilterParams } from "@org-common/lib/activities/activity-filter/activity-filter.component";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ActivityType, CulturalActivityMetadataByType, CulturalActivityTypeMetadata, CulturalActivityTypes, ICulturalActivityTypeMetadata, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { PersonFlagService } from "@org-common/lib/person/person-flag.service";
import { lastValueFrom, Subject } from "rxjs";
import { delay, switchMap, tap } from "rxjs/operators";

interface IActivityPage {
    toggleValue: ActivityPage;
    visible: boolean;
    icon: string;
    text: string;
}

const PageSearchParam = "page";

@Component({
    selector: "adapt-activity-page-content",
    templateUrl: "./activity-page-content.component.html",
    styleUrl: "./activity-page-content.component.scss",
    standalone: false,
})
export class ActivityPageContentComponent extends BaseRoutedComponent implements OnInit {
    public readonly ActivityPage = ActivityPage;
    public readonly ActivityStatus = CulturalActivityStatus;
    public readonly CulturalActivityType = CulturalActivityType;
    public readonly NonCulturalActivityType = NonCulturalActivityType;
    public readonly CulturalActivityMetadataByType = CulturalActivityMetadataByType;

    public readonly tabs: IActivityPage[] = [
        { toggleValue: ActivityPage.Pathway, icon: "fal fa-diamond", text: "Pathways", visible: this.isAlto },
        { toggleValue: ActivityPage.Activity, icon: "fal fa-list", text: "Activity", visible: true },
        { toggleValue: ActivityPage.Status, icon: "fal fa-wave-pulse", text: "Status", visible: true },
        { toggleValue: ActivityPage.Calendar, icon: "fal fa-calendar", text: "Calendar", visible: true },
    ];

    public readonly StatusSections: IActivityStatusSection[] = [
        {
            title: "Activities due",
            group: CulturalActivityStatus.Overdue,
            colourAccent: "var(--bs-danger)",
            displayedCount: 0,
            totalCount: 0,
        },
        {
            title: "Upcoming activities",
            group: CulturalActivityStatus.Upcoming,
            colourAccent: "var(--adapt-new-day-yellow-500)",
            displayedCount: 0,
            totalCount: 0,
        },
    ];

    // hq only has actions/meetings/objectives for now
    public readonly ActivityColumns = this.isAlto
        ? [
            [
                CulturalActivityMetadataByType[NonCulturalActivityType.Actions],
                CulturalActivityMetadataByType[NonCulturalActivityType.Meetings],
                CulturalActivityMetadataByType[NonCulturalActivityType.Objectives],
            ],
        ]
        : [
            [
                CulturalActivityMetadataByType[NonCulturalActivityType.Actions],
                CulturalActivityMetadataByType[NonCulturalActivityType.Meetings],
                CulturalActivityMetadataByType[NonCulturalActivityType.Objectives],
            ],
            [
                CulturalActivityMetadataByType[CulturalActivityType.CareerValuation],
                CulturalActivityMetadataByType[CulturalActivityType.CulturalIndex],
                CulturalActivityMetadataByType[CulturalActivityType.PeerCatchUp],
                CulturalActivityMetadataByType[CulturalActivityType.TeamAssessment],
                CulturalActivityMetadataByType[CulturalActivityType.EmployeeEngagementAssessment],
                CulturalActivityMetadataByType[CulturalActivityType.ResilientBusinessAssessment],
            ],
        ];

    public readonly activitySectionTooltip = `This number indicates the number of activities for this type shown on this dashboard.<br>Clicking on the heading will take you to a detail page with all the activities for this type.`;

    // trimmed down set of layout sizing options since these tend to overflow
    public readonly columnSizePresets: Partial<Record<ColumnSize, string[]>> = {
        [ColumnSize.FullWidth]: ["col-md-12"],
        [ColumnSize.Equal]: ["col-lg-6", "col-lg-6"],
    };
    public readonly componentSizePresets: Partial<Record<ComponentSize, string[]>> = {
        [ComponentSize.Full]: ["col-md-12", ""],
    };

    // all views
    public routes: IActivityRoutes = {};
    public activitiesGroupedByType = ActivitiesService.generateActivitiesGroupedByTypeObject();

    public anySectionIsLoading = true;
    public sectionIsLoading = new Map<ActivityType, boolean>(CulturalActivityTypes.map((type) => [type, true]));
    public sectionError = new Map<ActivityType, any>();
    public groupedErrors: IGroupedData<string, string>[] = [];

    private updateDataHasFired = false;
    private triggerUpdate = new Subject<ActivityType[]>();
    public shouldUpdate$ = this.triggerUpdate.asObservable();
    public updateLayout$ = new Subject<void>();

    // activity/status view
    // TODO: should this apply to calendar events?
    public blurPrivateActions = true;

    // status view
    public activitiesGroupedByStatus?: ICulturalActivitiesGroupedByStatusAndType;

    public constructor(
        injector: Injector,
        elementRef: ElementRef,
        personFlagService: PersonFlagService,
        public activitiesService: ActivitiesService,
        private activityPageService: ActivityPageService,
        private authorisationService: AuthorisationService,
        private commonDataService: CommonDataService,
        private userService: UserService,
    ) {
        super(injector, elementRef);

        // an activity handler has indicated that some activity types should be updated
        this.activitiesService.shouldUpdate$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(this.triggerUpdate);

        this.activitiesService.activityTypesLoaded$.pipe(
            delay(500),
            tap((loadedTypes) => this.setTypesLoading(loadedTypes, false)),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.activitiesService.activityTypesError$.pipe(
            tap(({ types, error }) => this.setTypesError(types, error)),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.triggerUpdate.pipe(
            switchMap((types) => this.triggerUpdateData(types)),
            this.takeUntilDestroyed(),
        ).subscribe();

        personFlagService.isFlagEnabled$(PersonFlag.BlurPrivateActions).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((blurPrivateActions) => this.blurPrivateActions = blurPrivateActions);
    }

    public get currentPerson() {
        return this.userService.currentPerson;
    }

    public get page() {
        return this.activityPageService.page;
    }

    public get hasAnyVisibleTypes() {
        return !!this.activityPageService.filter?.visibleTypes?.length;
    }

    public get hasAnyVisibleStatuses() {
        return !!this.activityPageService.filter?.visibleStatuses?.length;
    }

    public get hasAnyErrors() {
        return this.sectionError.size > 0;
    }

    public async ngOnInit() {
        // remove padding asap so page doesn't jump around
        this.removeDefaultShellPadding();

        // wait for the activitiesService to be ready before we activate the page
        // this gets all access permissions in organisationInitialisationActions
        await this.activitiesService.promiseToWaitUntilOrganisationInitialised();
        this.notifyActivated();

        // need to do in next digest cycle otherwise our url update is ignored
        // mostly relevant for going back to the activity page
        setTimeout(() => {
            const page = this.getSearchParameterValue(PageSearchParam)?.toLowerCase() as ActivityPage ?? this.page;
            this.updateUrl(Object.values(ActivityPage).includes(page) ? page : this.activityPageService.defaultPage);
        });
    }

    public async onActivityCompleted(activity: CulturalActivity) {
        this.commonDataService.setSavingInProgress(true);
        setTimeout(async () => {
            this.triggerUpdate.next([activity.type]);
            this.commonDataService.setSavingInProgress(false);
        }, 500);
    }

    public async updateUrl(selectedPage: ActivityPage) {
        const previousPage = this.page;
        this.activityPageService.page = selectedPage;

        const tab = this.tabs.find((t) => t.toggleValue === this.page);
        if (selectedPage === this.activityPageService.defaultPage || !tab?.visible) {
            await this.deleteSearchParameter(PageSearchParam);
        } else {
            await this.setSearchParameterValue(PageSearchParam, this.page);
        }

        // typically, onFilterChanged will cause update on page load.
        // however we don't update from triggerUpdate if this occurs when the page is the calendar.
        if (previousPage === ActivityPage.Calendar && !this.updateDataHasFired) {
            this.triggerUpdate.next(CulturalActivityTypes);
        }
    }

    public async onFilterChanged(filterParams: IActivityFilterParams) {
        this.activityPageService.filter = filterParams;
        this.triggerUpdate.next(CulturalActivityTypes);

        // tell layout manager to re-render, filter could have changed the visible components
        this.updateLayout$.next();
    }

    public sectionTrackBy(_index: number, section: ICulturalActivityTypeMetadata) {
        return section.type;
    }

    public keyValueTrackBy(_index: number, section: KeyValue<any, any>) {
        return section.key;
    }

    public statusSectionTrackBy(_index: number, section: IActivityStatusSection) {
        return section.group;
    }

    public activityTrackBy(_index: number, activity: IActivityEntityType) {
        return ActivitiesService.getKeyForActivityType(activity);
    }

    public getDisplayedCountForGroup(group: IActivityGroup<any>) {
        let displayedCount = group.displayedCount;

        if (!this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Overdue)) {
            displayedCount -= group[CulturalActivityStatus.Overdue].length;
        }

        if (!this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Upcoming)) {
            displayedCount -= group[CulturalActivityStatus.Upcoming].length;
        }

        return displayedCount;
    }

    public calculateDisplayedCountForStatus(section: IActivityStatusSection, count: number) {
        for (const [activityGroup, activities] of Object.entries(this.activitiesGroupedByStatus?.[section.group] ?? {})) {
            const meta = CulturalActivityMetadataByType[activityGroup as ActivityType];
            if (!this.activitiesService.isActivityTypeVisible(meta)) {
                for (const activity of activities) {
                    if (this.activitiesService.isObjectiveTeamGroup(activity)) {
                        count -= activity.objectives.length;
                    } else if (this.activitiesService.isMeetingTeamGroup(activity)) {
                        count -= activity.meetings.length;
                    } else {
                        count -= 1;
                    }
                }
            }
        }

        return count;
    }

    public getFallbackText(section: ICulturalActivityTypeMetadata) {
        const timeframe = this.activityPageService.filter?.dueWithin?.text ?? "1 month";
        return `Great work, you have no ${section.title.toLocaleLowerCase()} that are due or upcoming within ${timeframe}.`;
    }

    private async triggerUpdateData(activityTypesToUpdate: ActivityType[]) {
        this.anySectionIsLoading = true;
        this.setTypesLoading(activityTypesToUpdate, true);
        this.setTypesError(activityTypesToUpdate, undefined);

        // this gives the loading spinners a chance to show
        await new Promise(requestAnimationFrame);

        // calendar updates data by itself
        if (this.page !== ActivityPage.Calendar) {
            await this.updateData(activityTypesToUpdate);
        }
    }

    private setTypesLoading(activityTypesToUpdate: ActivityType[], isLoading: boolean) {
        activityTypesToUpdate.forEach((type) => this.sectionIsLoading.set(type, isLoading));
    }

    private setTypesError(activityTypesToUpdate: ActivityType[], error: any) {
        activityTypesToUpdate.forEach((type) => error
            ? this.sectionError.set(type, error)
            : this.sectionError.delete(type));

        this.groupedErrors = [];
        for (const [type, err] of this.sectionError.entries()) {
            const typeName = CulturalActivityMetadataByType[type].title;
            let group = this.groupedErrors.find((g) => g.key === err);
            if (!group) {
                group = { key: err, items: [typeName] };
                this.groupedErrors.push(group);
            } else {
                group.items.push(typeName);
            }
        }
    }

    private async updateData(activityTypesToUpdate: ActivityType[]) {
        this.updateDataHasFired = true;

        // build Activity view
        const activitiesGroupedByType = await this.activitiesService.getActivitiesGroupedByType(activityTypesToUpdate);
        Object.assign(this.activitiesGroupedByType, activitiesGroupedByType);

        // build Status view
        const {
            statusGroups,
            displayedCounts,
            totalCounts,
        } = this.activitiesService.groupActivitiesByStatus(this.activitiesGroupedByType);
        this.activitiesGroupedByStatus = statusGroups;

        for (const section of this.StatusSections) {
            section.displayedCount = this.calculateDisplayedCountForStatus(section, displayedCounts[section.group]);
            section.totalCount = totalCounts[section.group];
        }

        // populate routes
        const personIsCulturalLeader = await this.authorisationService.promiseToGetHasAccess(CulturalLeadershipFrameworkAuthService.ConfigureCohorts);
        for (const culturalActivityType of CulturalActivityTypeMetadata) {
            if (culturalActivityType.route$ && !culturalActivityType.routeRequiresTeamId) {
                this.routes[culturalActivityType.type] = await lastValueFrom(culturalActivityType.route$(undefined, personIsCulturalLeader));
            }
        }

        setTimeout(() => this.anySectionIsLoading = false, 250);
    }
}
